import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/user-management.css";
import "../styles/users-3d-management.css";
import FormModal from "../components/ui/FormModal";
import ConfirmModal from "../components/ui/ConfirmModal";
import {
  get3DUsers,
  get3DUsersBasicInfo,
  create3DUser,
  patch3DUser,
  delete3DUser,
  link3DUser,
} from "../services/users3dService";
import { getCompanyNames } from "../services/companyService";
import { getUsersBasicInfo } from "../services/userService";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const emptyCreate = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  company_id: "",
};

const Users3DManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");

  const [loading, setLoading] = useState(true);
  const [users3d, setUsers3d] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState(
    () => (searchParams.get("search") || "").trim(),
  );
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [createError, setCreateError] = useState("");
  const [createCompanies, setCreateCompanies] = useState([]);
  const [createCompaniesLoading, setCreateCompaniesLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Link Account states
  const [linkOptionsOpen, setLinkOptionsOpen] = useState(false); // Initial options modal

  // For "Link Your Own Account" flow
  const [linkOwnModalOpen, setLinkOwnModalOpen] = useState(false);
  const [linkSearchTerm, setLinkSearchTerm] = useState("");
  const [link3dUsers, setLink3dUsers] = useState([]);
  const [link3dUsersLoading, setLink3dUsersLoading] = useState(false);
  const [selectedLinkUserId, setSelectedLinkUserId] = useState(null);
  const [confirmLinkOpen, setConfirmLinkOpen] = useState(false);
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);

  // For "Link Other User's Account" flow
  const [linkOtherStep, setLinkOtherStep] = useState(1); // 1 = select CMS user, 2 = select 3D user
  const [linkOtherModalOpen, setLinkOtherModalOpen] = useState(false);
  const [cmsUsers, setCmsUsers] = useState([]);
  const [cmsUsersLoading, setCmsUsersLoading] = useState(false);
  const [linkOtherUserSearch, setLinkOtherUserSearch] = useState("");
  const [selectedCmsUser, setSelectedCmsUser] = useState(null); // The CMS user to link
  const [linkOther3dSearch, setLinkOther3dSearch] = useState("");
  const [selectedOther3dUser, setSelectedOther3dUser] = useState(null);
  const [confirmLinkOtherOpen, setConfirmLinkOtherOpen] = useState(false);
  const [confirmUnlinkOtherOpen, setConfirmUnlinkOtherOpen] = useState(false);
  const [unlinkingCmsUserId, setUnlinkingCmsUserId] = useState(null);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    company_id: "",
  });
  const [editCompanies, setEditCompanies] = useState([]);
  const [editCompaniesLoading, setEditCompaniesLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const companies = useMemo(() => {
    const byId = new Map();
    (users3d || []).forEach((u) => {
      const raw = u?.company;
      const companyId = Number(
        raw?.company_id ?? u?.company_id ?? raw?.id ?? NaN,
      );
      if (!Number.isInteger(companyId) || byId.has(companyId)) return;
      const name =
        raw?.name ||
        raw?.companyName ||
        u?.company_name ||
        `Company ${companyId}`;
      byId.set(companyId, {
        company_id: companyId,
        name: String(name || ""),
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
  }, [users3d]);

  const companyNameById = useMemo(() => {
    const m = new Map();
    companies.forEach((c) => m.set(Number(c.company_id), c.name));
    return m;
  }, [companies]);

  const regularUsers = useMemo(() => {
    const byId = new Map();
    const register = (rawUser) => {
      const userId = Number(rawUser?.user_id ?? rawUser?.id ?? NaN);
      if (!Number.isInteger(userId) || byId.has(userId)) return;
      byId.set(userId, {
        user_id: userId,
        name: rawUser?.name || rawUser?.full_name || "",
        email: rawUser?.email || "",
      });
    };

    (users3d || []).forEach((u) => {
      register(u?.creator);
      register(u?.updater);
      register(u?.linkedUser);
    });

    return Array.from(byId.values()).sort((a, b) => {
      const an = String(a.name || a.email || "");
      const bn = String(b.name || b.email || "");
      return an.localeCompare(bn);
    });
  }, [users3d]);

  const userNameById = useMemo(() => {
    const m = new Map();
    regularUsers.forEach((u) => {
      const id = Number(u.user_id);
      const name = u.name || u.email || `User ${id}`;
      m.set(id, name);
    });
    return m;
  }, [regularUsers]);

  // Create a map to get user details by ID for created_by lookup
  const getUserName = useCallback(
    (userId) => {
      if (!userId || userId === null || userId === undefined) return "—";
      const numericUserId = Number(userId);
      const userName = userNameById.get(numericUserId);
      if (userName) return userName;
      return "—";
    },
    [userNameById],
  );

  // Search/filtering is server-side; users3d already contains the filtered page.

  const linkable3dUsers = useMemo(
    () =>
      Array.isArray(link3dUsers) && link3dUsers.length > 0 ? link3dUsers : users3d,
    [link3dUsers, users3d],
  );

  const get3dCompanyName = useCallback(
    (user3d) =>
      user3d?.company?.name ||
      user3d?.company?.companyName ||
      companyNameById.get(Number(user3d?.company_id)) ||
      "",
    [companyNameById],
  );

  // Filtered users for Link Account modal
  const filteredLinkUsers = useMemo(() => {
    const q = (linkSearchTerm || "").toLowerCase().trim();
    if (!q) return linkable3dUsers;
    return (linkable3dUsers || []).filter((u) => {
      const name = String(u.name || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const company = String(get3dCompanyName(u) || "").toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [linkable3dUsers, linkSearchTerm, get3dCompanyName]);

  // Get current user ID from session
  const currentUserId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("auth");
      const auth = raw ? JSON.parse(raw) : null;
      return auth?.user?.user_id ? Number(auth.user.user_id) : null;
    } catch {
      return null;
    }
  }, []);

  // Derive userLinks from users3d data (Map<cmsUserId, 3dUserId>)
  const userLinks = useMemo(() => {
    const m = new Map();
    (linkable3dUsers || []).forEach((u) => {
      if (u.user_id != null) {
        m.set(Number(u.user_id), Number(u.id));
      }
    });
    return m;
  }, [linkable3dUsers]);

  // Get linked 3D user for current user's own account
  const myLinked3dUserId = useMemo(() => {
    if (!currentUserId) return null;
    return userLinks.get(currentUserId) || null;
  }, [currentUserId, userLinks]);

  const myLinkedUser = useMemo(() => {
    if (!myLinked3dUserId) return null;
    return (
      linkable3dUsers.find((u) => Number(u.id) === Number(myLinked3dUserId)) ||
      users3d.find((u) => Number(u.id) === Number(myLinked3dUserId)) ||
      null
    );
  }, [linkable3dUsers, users3d, myLinked3dUserId]);

  // Filtered CMS users for "Link Other User" flow
  const filteredCmsUsers = useMemo(() => {
    const q = (linkOtherUserSearch || "").toLowerCase().trim();
    if (!q) return cmsUsers;
    return (cmsUsers || []).filter((u) => {
      const name = String(u.name || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [cmsUsers, linkOtherUserSearch]);

  // Filtered 3D users for "Link Other User" step 2
  const filteredOther3dUsers = useMemo(() => {
    const q = (linkOther3dSearch || "").toLowerCase().trim();
    if (!q) return linkable3dUsers;
    return (linkable3dUsers || []).filter((u) => {
      const name = String(u.name || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const company = String(get3dCompanyName(u) || "").toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [linkable3dUsers, linkOther3dSearch, get3dCompanyName]);

  // Check if a 3D user is already linked to any CMS user
  const get3dUserLinkedTo = (user3dId) => {
    for (const [cmsId, linked3dId] of userLinks.entries()) {
      if (Number(linked3dId) === Number(user3dId)) {
        return cmsId;
      }
    }
    return null;
  };

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await get3DUsers({ page, limit, search: debouncedSearch });
      setUsers3d(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || { total: 0, page: Number(page) || 1, limit: Number(limit) || 10, totalPages: 1 },
      );
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e?.message || "Failed to load 3D users");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = (searchInput || "").trim();
    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");
    if (searchParams.toString() !== params.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  // Fire reload (deduped)
  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".actions-menu-wrapper")) setActiveDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setActiveDropdown(null); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const allSelected = useMemo(
    () => users3d.length > 0 && selectedIds.size === users3d.length,
    [users3d, selectedIds],
  );

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(Math.max(1, Number(pagination.page) || page), totalPages);
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const cur = Number(limit);
    const all = Number.isInteger(cur) && cur > 0 ? [...base, cur] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users3d.map((u) => u.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const startEdit = async (user) => {
    setEditCompaniesLoading(true);
    try {
      const list = await getCompanyNames();
      setEditCompanies(Array.isArray(list) ? list : []);
    } catch {
      setEditCompanies(companies);
    } finally {
      setEditCompaniesLoading(false);
    }

    // Ensure we're storing the ID as a number for strict comparison
    setEditingId(Number(user.id));
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      company_id: String(user.company_id ?? ""),
    });
    setEditOpen(true);
  };

  const cancelEdit = () => {
    setEditOpen(false);
    setEditingId(null);
    setEditCompanies([]);
    setEditCompaniesLoading(false);
    setEditForm({ name: "", email: "", company_id: "" });
  };

  const saveEdit = async (idOverride) => {
    const rawId =
      idOverride !== undefined && idOverride !== null ? idOverride : editingId;
    if (rawId === null || rawId === undefined) {
      toast.error("No 3D user selected for editing");
      return;
    }
    const idNum = Number(rawId);
    if (!Number.isInteger(idNum)) {
      toast.error("Invalid 3D user id");
      return;
    }
    const payload = {
      name: (editForm.name || "").trim(),
      email: (editForm.email || "").trim(),
      company_id: editForm.company_id ? Number(editForm.company_id) : null,
    };
    if (!payload.name) {
      toast.error("Name is required");
      return;
    }
    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      toast.error("Valid email is required");
      return;
    }
    if (!Number.isInteger(Number(editForm.company_id))) {
      toast.error("Company is required");
      return;
    }
    try {
      const updated = await patch3DUser(idNum, payload);
      const merged = updated && typeof updated === "object" ? updated : payload;
      setUsers3d((prev) =>
        prev.map((u) => (Number(u.id) === idNum ? { ...u, ...merged } : u)),
      );
      toast.success(`3D User "${payload.name}" has been updated successfully!`);
      cancelEdit();
    } catch (e) {
      console.error("Update 3D user error:", e);
      toast.error(e?.message || "Failed to update 3D user");
    }
  };

  const removeSingle = async (id) => {
    if (!id) {
      toast.error("Invalid user ID");
      return;
    }
    const user = users3d.find((u) => Number(u.id) === Number(id));
    const userName = user?.name || user?.email || "3D User";
    setConfirmDelete({
      open: true,
      id: Number(id), // Ensure ID is stored as a number
      name: userName,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id } = confirmDelete;
    if (!id) {
      toast.error("No user selected for deletion");
      setConfirmDelete({ open: false, id: null, name: "" });
      return;
    }

    // Close modal first
    setConfirmDelete({ open: false, id: null, name: "" });

    try {
      await delete3DUser(id);
      toast.success(`3D User has been deleted successfully!`);
      setUsers3d((prev) => prev.filter((u) => u.id !== id));
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    } catch (e) {
      toast.error(e?.message || "Failed to delete 3D user");
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setConfirmDelete({ open: false, id: null, name: "" });

    try {
      for (const id of ids) {
        // eslint-disable-line no-restricted-syntax
        // eslint-disable-next-line no-await-in-loop
        await delete3DUser(id);
      }
      toast.success(`${ids.length} 3D User(s) have been deleted successfully!`);
      setUsers3d((prev) => prev.filter((u) => !selectedIds.has(u.id)));
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e?.message || "Failed to delete selected 3D users");
    }
  };

  const openCreate = async () => {
    setCreateForm({ ...emptyCreate });
    setCreateError("");
    setCreateCompaniesLoading(true);
    try {
      const list = await getCompanyNames();
      setCreateCompanies(Array.isArray(list) ? list : []);
    } catch {
      setCreateCompanies(companies);
    } finally {
      setCreateCompaniesLoading(false);
    }
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateCompanies([]);
    setCreateCompaniesLoading(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // Link Account handlers - Options Modal
  const openLinkOptions = () => {
    setLinkOptionsOpen(true);
  };

  const closeLinkOptions = () => {
    setLinkOptionsOpen(false);
  };

  // === Link Your Own Account handlers ===
  const openLinkOwnModal = async () => {
    closeLinkOptions();
    setLinkSearchTerm("");
    setSelectedLinkUserId(null);
    setLink3dUsersLoading(true);
    try {
      const userList = await get3DUsersBasicInfo();
      setLink3dUsers(Array.isArray(userList) ? userList : []);
    } catch (e) {
      setLink3dUsers([]);
      toast.error(e?.message || "Failed to load 3D users");
    } finally {
      setLink3dUsersLoading(false);
    }
    setLinkOwnModalOpen(true);
  };

  const closeLinkOwnModal = () => {
    setLinkOwnModalOpen(false);
    setLinkSearchTerm("");
    setSelectedLinkUserId(null);
    setLink3dUsers([]);
    setLink3dUsersLoading(false);
  };

  const handleSelectLinkUser = (userId) => {
    // Can only select if not already linked to another CMS user
    const linkedToCms = get3dUserLinkedTo(userId);
    if (linkedToCms && linkedToCms !== currentUserId) return;
    setSelectedLinkUserId(Number(userId));
    setConfirmLinkOpen(true);
  };

  const confirmLinkAccount = async () => {
    if (!currentUserId || !selectedLinkUserId) {
      setConfirmLinkOpen(false);
      return;
    }
    try {
      await link3DUser(selectedLinkUserId, currentUserId);
      // Update local state
      setUsers3d((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(selectedLinkUserId) ?
            { ...u, user_id: currentUserId }
          : u,
        ),
      );
      setLink3dUsers((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(selectedLinkUserId) ?
            { ...u, user_id: currentUserId }
          : u,
        ),
      );
      toast.success("Account linked successfully!");
    } catch (e) {
      toast.error(e?.message || "Failed to link account");
    }
    setConfirmLinkOpen(false);
    closeLinkOwnModal();
  };

  const handleUnlinkOwnAccount = () => {
    setConfirmUnlinkOpen(true);
  };

  const confirmUnlinkOwnAccount = async () => {
    if (!currentUserId || !myLinked3dUserId) {
      setConfirmUnlinkOpen(false);
      return;
    }
    try {
      await link3DUser(myLinked3dUserId, null);
      // Update local state
      setUsers3d((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(myLinked3dUserId) ?
            { ...u, user_id: null }
          : u,
        ),
      );
      setLink3dUsers((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(myLinked3dUserId) ?
            { ...u, user_id: null }
          : u,
        ),
      );
      toast.success("Account unlinked successfully!");
    } catch (e) {
      toast.error(e?.message || "Failed to unlink account");
    }
    setConfirmUnlinkOpen(false);
  };

  // === Link Other User's Account handlers ===
  const openLinkOtherModal = async () => {
    closeLinkOptions();
    setLinkOtherStep(1);
    setLinkOtherUserSearch("");
    setSelectedCmsUser(null);
    setLinkOther3dSearch("");
    setSelectedOther3dUser(null);
    setCmsUsersLoading(true);
    setLink3dUsersLoading(true);
    try {
      const [cmsList, user3dList] = await Promise.all([
        getUsersBasicInfo(),
        get3DUsersBasicInfo(),
      ]);
      setCmsUsers(Array.isArray(cmsList) ? cmsList : []);
      setLink3dUsers(Array.isArray(user3dList) ? user3dList : []);
    } catch (e) {
      setCmsUsers([]);
      setLink3dUsers([]);
      toast.error(e?.message || "Failed to load CMS users");
    } finally {
      setCmsUsersLoading(false);
      setLink3dUsersLoading(false);
    }
    setLinkOtherModalOpen(true);
  };

  const closeLinkOtherModal = () => {
    setLinkOtherModalOpen(false);
    setLinkOtherStep(1);
    setCmsUsers([]);
    setCmsUsersLoading(false);
    setLinkOtherUserSearch("");
    setSelectedCmsUser(null);
    setLinkOther3dSearch("");
    setSelectedOther3dUser(null);
    setLink3dUsers([]);
    setLink3dUsersLoading(false);
  };

  const handleSelectCmsUser = (user) => {
    setSelectedCmsUser(user);
    setLinkOtherStep(2);
    setLinkOther3dSearch("");
  };

  const handleBackToStep1 = () => {
    setLinkOtherStep(1);
    setSelectedOther3dUser(null);
    setLinkOther3dSearch("");
  };

  const handleSelectOther3dUser = (userId) => {
    // Check if already linked to another CMS user
    const linkedToCms = get3dUserLinkedTo(userId);
    if (
      linkedToCms &&
      selectedCmsUser &&
      linkedToCms !== Number(selectedCmsUser.user_id)
    )
      return;
    setSelectedOther3dUser(Number(userId));
    setConfirmLinkOtherOpen(true);
  };

  const confirmLinkOtherAccount = async () => {
    if (!selectedCmsUser || !selectedOther3dUser) {
      setConfirmLinkOtherOpen(false);
      return;
    }
    const cmsId = Number(selectedCmsUser.user_id);
    try {
      await link3DUser(selectedOther3dUser, cmsId);
      // Update local state
      setUsers3d((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(selectedOther3dUser) ?
            { ...u, user_id: cmsId }
          : u,
        ),
      );
      setLink3dUsers((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(selectedOther3dUser) ?
            { ...u, user_id: cmsId }
          : u,
        ),
      );
      toast.success(`Account linked successfully!`);
    } catch (e) {
      toast.error(e?.message || "Failed to link account");
    }
    setConfirmLinkOtherOpen(false);
    closeLinkOtherModal();
  };

  const handleUnlinkOtherAccount = (cmsUserId) => {
    setUnlinkingCmsUserId(cmsUserId);
    setConfirmUnlinkOtherOpen(true);
  };

  const confirmUnlinkOtherAccount = async () => {
    if (!unlinkingCmsUserId) {
      setConfirmUnlinkOtherOpen(false);
      setUnlinkingCmsUserId(null);
      return;
    }
    // Find the 3D user linked to this CMS user
    const linked3dUser = linkable3dUsers.find(
      (u) => Number(u.user_id) === Number(unlinkingCmsUserId),
    );
    if (!linked3dUser) {
      setConfirmUnlinkOtherOpen(false);
      setUnlinkingCmsUserId(null);
      return;
    }
    try {
      await link3DUser(linked3dUser.id, null);
      // Update local state
      setUsers3d((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(linked3dUser.id) ?
            { ...u, user_id: null }
          : u,
        ),
      );
      setLink3dUsers((prev) =>
        prev.map((u) =>
          Number(u.id) === Number(linked3dUser.id) ?
            { ...u, user_id: null }
          : u,
        ),
      );
      toast.success("Account unlinked successfully!");
    } catch (e) {
      toast.error(e?.message || "Failed to unlink account");
    }
    setConfirmUnlinkOtherOpen(false);
    setUnlinkingCmsUserId(null);
  };

  const handleCreate = async () => {
    try {
      setCreateError("");

      const payload = {
        name: (createForm.name || "").trim(),
        email: (createForm.email || "").trim(),
        password: (createForm.password || "").trim(),
        confirmPassword: (createForm.confirmPassword || "").trim(),
        company_id:
          createForm.company_id ? Number(createForm.company_id) : null,
      };

      if (!payload.name) {
        setCreateError("Name is required");
        return;
      }
      if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
        setCreateError("Valid email is required");
        return;
      }
      if (!payload.password) {
        setCreateError("Password is required");
        return;
      }
      if (!payload.confirmPassword) {
        setCreateError("Confirm password is required");
        return;
      }
      if (payload.password !== payload.confirmPassword) {
        setCreateError("Passwords do not match");
        return;
      }
      if (!Number.isInteger(Number(createForm.company_id))) {
        setCreateError("Company is required");
        return;
      }

      await create3DUser(payload);
      await reload();
      toast.success(`3D User "${payload.name}" has been created successfully!`);
      closeCreate();
    } catch (e) {
      setCreateError(e?.message || "Failed to create 3D user");
    }
  };

  return (
    <div className="page-container users-3d-management">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Manage 3D Users</h2>
            <p className="page-subtitle">
              View all 3D users and create new ones
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by name, email, company, created by"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button type="button" className="primary-btn" onClick={openCreate}>
              Create 3D User
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={openLinkOptions}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Link Accounts
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="job-pagination-top">
          <label htmlFor="u3d-limit" className="job-pagination-label">
            Rows per page:
          </label>
          <select
            id="u3d-limit"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value) || 10);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "#eff6ff",
              borderRadius: "0.75rem",
              border: "1px solid #bfdbfe",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span style={{ fontWeight: "600", color: "#1e40af", fontSize: "0.9375rem" }}>
                {selectedIds.size}{" "}
                {selectedIds.size === 1 ? "3D user" : "3D users"} selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button className="danger-btn sm" onClick={bulkDelete}>
                  Delete Selected
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}
        {loading ?
          <div className="no-results">Loading 3D users…</div>
        : <div className="users-table">
            <div className="users-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Name</div>
              <div className="cell">Email</div>
              <div className="cell">Company</div>
              <div className="cell">Created By</div>
              <div className="cell actions">Actions</div>
            </div>
            {users3d.length === 0 ?
              <div className="no-results">No 3D users found</div>
            : users3d.map((u) => (
                <div
                  key={u.id}
                  className={`users-table-row ${
                    selectedIds.has(u.id) ? "selected" : ""
                  }`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(u.id)}
                      onChange={() => toggleSelect(u.id)}
                    />
                  </div>
                  <div className="cell">{u.name}</div>
                  <div
                    className="cell"
                    style={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {u.email || "-"}
                  </div>
                  <div className="cell">
                    {companyNameById.get(Number(u.company_id)) || "—"}
                  </div>
                  <div className="cell">{getUserName(u.created_by)}</div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === u.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === u.id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(getActionDropdownDirection(e.currentTarget));
                            setActiveDropdown(u.id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === u.id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              startEdit(u);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              removeSingle(u.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <div className="job-pagination-bottom">
          <div className="job-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="job-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span key={`ellipsis-${index}`} style={{ padding: "0 6px", color: "#6b7280" }} aria-hidden="true">
                    …
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={p === currentPage ? "primary-btn sm" : "secondary-btn sm"}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <FormModal
        open={editOpen}
        title="Edit 3D User"
        size="compact"
        onCancel={cancelEdit}
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={() => saveEdit()}
            >
              Save
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </>
        }
      >
        <div className="role-card create-card">
          <div className="field">
            <label htmlFor="eu3d-name">Name</label>
            <input
              id="eu3d-name"
              type="text"
              value={editForm.name}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, name: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label htmlFor="eu3d-email">Email</label>
            <input
              id="eu3d-email"
              type="email"
              value={editForm.email}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, email: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label htmlFor="eu3d-company">Company</label>
            <select
              id="eu3d-company"
              value={editForm.company_id}
              onChange={(e) =>
                setEditForm((f) => ({ ...f, company_id: e.target.value }))
              }
            >
              <option value="">
                {editCompaniesLoading ?
                  "Loading companies..."
                : "Select company"}
              </option>
              {(editCompanies.length > 0 ? editCompanies : companies).map(
                (c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ),
              )}
            </select>
          </div>
        </div>
      </FormModal>

      <FormModal
        open={createOpen}
        title="Create 3D User"
        size="compact"
        onCancel={closeCreate}
        footer={
          <>
            <button
              type="button"
              className="primary-btn"
              onClick={handleCreate}
            >
              Create
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={closeCreate}
            >
              Cancel
            </button>
          </>
        }
      >
        {createError && (
          <div className="error-banner" style={{ marginBottom: 12 }}>
            {createError}
          </div>
        )}
        <div className="role-card create-card">
          <div className="field">
            <label htmlFor="cu3d-name">Name</label>
            <input
              id="cu3d-name"
              type="text"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm({ ...createForm, name: e.target.value })
              }
            />
          </div>
          <div className="field">
            <label htmlFor="cu3d-email">Email</label>
            <input
              id="cu3d-email"
              type="email"
              value={createForm.email}
              onChange={(e) =>
                setCreateForm({ ...createForm, email: e.target.value })
              }
            />
          </div>
          <div className="field">
            <label htmlFor="cu3d-password">Password</label>
            <div style={{ position: "relative" }}>
              <input
                id="cu3d-password"
                type={showPassword ? "text" : "password"}
                value={createForm.password}
                onChange={(e) =>
                  setCreateForm({ ...createForm, password: e.target.value })
                }
                style={{ paddingRight: "40px" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 8px",
                  color: "#6b7280",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={showPassword ? "Hide password" : "Show password"}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ?
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                : <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                }
              </button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="cu3d-confirm">Confirm Password</label>
            <div style={{ position: "relative" }}>
              <input
                id="cu3d-confirm"
                type={showConfirmPassword ? "text" : "password"}
                value={createForm.confirmPassword}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    confirmPassword: e.target.value,
                  })
                }
                style={{ paddingRight: "40px" }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 8px",
                  color: "#6b7280",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={showConfirmPassword ? "Hide password" : "Show password"}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ?
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                : <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                }
              </button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="cu3d-company">Company</label>
            <select
              id="cu3d-company"
              value={createForm.company_id}
              onChange={(e) =>
                setCreateForm({ ...createForm, company_id: e.target.value })
              }
            >
              <option value="">
                {createCompaniesLoading ?
                  "Loading companies..."
                : "Select company"}
              </option>
              {(createCompanies.length > 0 ? createCompanies : companies).map(
                (c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {c.name}
                  </option>
                ),
              )}
            </select>
          </div>
        </div>
      </FormModal>

      <ConfirmModal
        open={confirmDelete.open}
        title={confirmDelete.isBulk ? "Confirm Bulk Delete" : "Confirm Delete"}
        message={
          confirmDelete.isBulk ?
            `Are you sure you want to delete ${confirmDelete.count} ${
              confirmDelete.count === 1 ? "3D user" : "3D users"
            }? This action cannot be undone.`
          : `Are you sure you want to delete the 3D user "${confirmDelete.name}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={
          confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
        }
        onCancel={() => setConfirmDelete({ open: false, id: null, name: "" })}
        isDanger={true}
      />

      {/* Link Options Modal - Choose between Own Account or Other User */}
      <FormModal
        open={linkOptionsOpen}
        title="Link Accounts"
        size="compact"
        onCancel={closeLinkOptions}
        footer={
          <button
            type="button"
            className="secondary-btn"
            onClick={closeLinkOptions}
          >
            Cancel
          </button>
        }
      >
        <div className="link-options-modal">
          <p className="link-options-description">
            Choose how you want to link accounts:
          </p>
          <div className="link-options-buttons">
            <button
              type="button"
              className="link-option-card"
              onClick={openLinkOwnModal}
            >
              <div className="link-option-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <div className="link-option-content">
                <strong>Link Your Own Account</strong>
                <span>Connect your CMS account to a 3D user</span>
              </div>
              {myLinkedUser && (
                <span className="link-option-status linked">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Linked
                </span>
              )}
            </button>
            <button
              type="button"
              className="link-option-card"
              onClick={openLinkOtherModal}
            >
              <div className="link-option-icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className="link-option-content">
                <strong>Link Other User's Account</strong>
                <span>Connect another CMS user to a 3D user</span>
              </div>
              {userLinks.size > (myLinked3dUserId ? 1 : 0) && (
                <span className="link-option-status info">
                  {userLinks.size - (myLinked3dUserId ? 1 : 0)} linked
                </span>
              )}
            </button>
          </div>
        </div>
      </FormModal>

      {/* Link Your Own Account Modal */}
      <FormModal
        open={linkOwnModalOpen}
        title="Link Your Own Account"
        size="compact"
        onCancel={closeLinkOwnModal}
        footer={
          <button
            type="button"
            className="secondary-btn"
            onClick={closeLinkOwnModal}
          >
            Close
          </button>
        }
      >
        <div className="link-account-modal">
          {myLinkedUser && (
            <div className="linked-account-banner">
              <div className="linked-info">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
                <div>
                  <strong>Currently Linked:</strong>
                  <span>
                    {myLinkedUser.name} ({myLinkedUser.email})
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="danger-btn sm"
                onClick={handleUnlinkOwnAccount}
              >
                Unlink
              </button>
            </div>
          )}

          <div className="link-search-section">
            <input
              className="search-input"
              type="text"
              placeholder="Search 3D users by name, email, company..."
              value={linkSearchTerm}
              onChange={(e) => setLinkSearchTerm(e.target.value)}
            />
          </div>

          <div className="link-users-list">
            {link3dUsersLoading ?
              <div className="no-results">Loading 3D users…</div>
            : filteredLinkUsers.length === 0 ?
              <div className="no-results">No 3D users found</div>
            : filteredLinkUsers.map((u) => {
                const isMyLinked =
                  myLinked3dUserId && Number(myLinked3dUserId) === Number(u.id);
                const linkedToCmsId = get3dUserLinkedTo(u.id);
                const isLinkedToOther =
                  linkedToCmsId && linkedToCmsId !== currentUserId;
                const isDisabled =
                  isLinkedToOther || (myLinked3dUserId && !isMyLinked);
                return (
                  <div
                    key={u.id}
                    className={`link-user-row ${isMyLinked ? "linked" : ""} ${
                      isDisabled ? "disabled" : ""
                    }`}
                    onClick={() =>
                      !isDisabled && !isMyLinked && handleSelectLinkUser(u.id)
                    }
                  >
                    <div className="link-user-info">
                      <div className="link-user-name">{u.name}</div>
                      <div className="link-user-email">{u.email}</div>
                      <div className="link-user-company">
                        {get3dCompanyName(u) || "No Company"}
                      </div>
                    </div>
                    <div className="link-user-actions">
                      {isMyLinked ?
                        <span className="linked-badge">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          Linked
                        </span>
                      : isLinkedToOther ?
                        <span className="disabled-text">
                          Linked to another user
                        </span>
                      : isDisabled ?
                        <span className="disabled-text">Unavailable</span>
                      : <button
                          type="button"
                          className="primary-btn sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectLinkUser(u.id);
                          }}
                        >
                          Select
                        </button>
                      }
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      </FormModal>

      {/* Link Other User's Account Modal */}
      <FormModal
        open={linkOtherModalOpen}
        title={
          linkOtherStep === 1 ? "Select CMS User" : (
            `Select 3D User for ${selectedCmsUser?.name || "User"}`
          )
        }
        size="compact"
        onCancel={closeLinkOtherModal}
        footer={
          <>
            {linkOtherStep === 2 && (
              <button
                type="button"
                className="secondary-btn"
                onClick={handleBackToStep1}
              >
                ← Back
              </button>
            )}
            <button
              type="button"
              className="secondary-btn"
              onClick={closeLinkOtherModal}
            >
              Close
            </button>
          </>
        }
      >
        <div className="link-account-modal">
          {linkOtherStep === 1 ?
            <>
              <div className="link-search-section">
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search CMS users by name, email..."
                  value={linkOtherUserSearch}
                  onChange={(e) => setLinkOtherUserSearch(e.target.value)}
                />
              </div>

              <div className="link-users-list">
                {cmsUsersLoading ?
                  <div className="no-results">Loading CMS users…</div>
                : filteredCmsUsers.length === 0 ?
                  <div className="no-results">No CMS users found</div>
                : filteredCmsUsers.map((u) => {
                    const cmsId = Number(u.user_id);
                    const linked3dId = userLinks.get(cmsId);
                    const linked3dUser =
                      linked3dId ?
                        linkable3dUsers.find(
                          (x) => Number(x.id) === Number(linked3dId),
                        )
                      : null;
                    return (
                      <div
                        key={u.user_id}
                        className={`link-user-row ${
                          linked3dId ? "has-link" : ""
                        }`}
                        onClick={() => handleSelectCmsUser(u)}
                      >
                        <div className="link-user-info">
                          <div className="link-user-name">
                            {u.name || u.email}
                          </div>
                          <div className="link-user-email">{u.email}</div>
                          {linked3dUser && (
                            <div className="link-user-linked-to">
                              Linked to: {linked3dUser.name}
                            </div>
                          )}
                        </div>
                        <div className="link-user-actions">
                          {linked3dId ?
                            <button
                              type="button"
                              className="danger-btn sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnlinkOtherAccount(cmsId);
                              }}
                            >
                              Unlink
                            </button>
                          : <button
                              type="button"
                              className="primary-btn sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectCmsUser(u);
                              }}
                            >
                              Select
                            </button>
                          }
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </>
          : <>
              <div className="selected-cms-user-banner">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>
                  Linking for:{" "}
                  <strong>
                    {selectedCmsUser?.name || selectedCmsUser?.email}
                  </strong>
                </span>
              </div>

              <div className="link-search-section">
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search 3D users by name, email, company..."
                  value={linkOther3dSearch}
                  onChange={(e) => setLinkOther3dSearch(e.target.value)}
                />
              </div>

              <div className="link-users-list">
                {link3dUsersLoading ?
                  <div className="no-results">Loading 3D users…</div>
                : filteredOther3dUsers.length === 0 ?
                  <div className="no-results">No 3D users found</div>
                : filteredOther3dUsers.map((u) => {
                    const linkedToCmsId = get3dUserLinkedTo(u.id);
                    const isLinkedToSelected =
                      linkedToCmsId &&
                      selectedCmsUser &&
                      linkedToCmsId === Number(selectedCmsUser.user_id);
                    const isLinkedToOther =
                      linkedToCmsId && !isLinkedToSelected;
                    return (
                      <div
                        key={u.id}
                        className={`link-user-row ${
                          isLinkedToSelected ? "linked" : ""
                        } ${isLinkedToOther ? "disabled" : ""}`}
                        onClick={() =>
                          !isLinkedToOther &&
                          !isLinkedToSelected &&
                          handleSelectOther3dUser(u.id)
                        }
                      >
                        <div className="link-user-info">
                          <div className="link-user-name">{u.name}</div>
                          <div className="link-user-email">{u.email}</div>
                          <div className="link-user-company">
                            {get3dCompanyName(u) || "No Company"}
                          </div>
                        </div>
                        <div className="link-user-actions">
                          {isLinkedToSelected ?
                            <span className="linked-badge">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              Linked
                            </span>
                          : isLinkedToOther ?
                            <span className="disabled-text">
                              Linked to another user
                            </span>
                          : <button
                              type="button"
                              className="primary-btn sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectOther3dUser(u.id);
                              }}
                            >
                              Select
                            </button>
                          }
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </>
          }
        </div>
      </FormModal>

      {/* Confirm Link Own Account Modal */}
      <ConfirmModal
        open={confirmLinkOpen}
        title="Confirm Link Account"
        message={
          selectedLinkUserId ?
            `Are you sure you want to link your CMS account to the 3D user "${
              users3d.find((u) => Number(u.id) === Number(selectedLinkUserId))
                ?.name || "Unknown"
            }"? You can only link to one 3D user account at a time.`
          : "Please select a 3D user to link."
        }
        confirmLabel="Link Account"
        cancelLabel="Cancel"
        onConfirm={confirmLinkAccount}
        onCancel={() => setConfirmLinkOpen(false)}
        isDanger={false}
      />

      {/* Confirm Unlink Own Account Modal */}
      <ConfirmModal
        open={confirmUnlinkOpen}
        title="Confirm Unlink Account"
        message={
          myLinkedUser ?
            `Are you sure you want to unlink your CMS account from the 3D user "${myLinkedUser.name}"?`
          : "No account is currently linked."
        }
        confirmLabel="Unlink"
        cancelLabel="Cancel"
        onConfirm={confirmUnlinkOwnAccount}
        onCancel={() => setConfirmUnlinkOpen(false)}
        isDanger={true}
      />

      {/* Confirm Link Other User's Account Modal */}
      <ConfirmModal
        open={confirmLinkOtherOpen}
        title="Confirm Link Account"
        message={
          selectedCmsUser && selectedOther3dUser ?
            `Are you sure you want to link the CMS user "${
              selectedCmsUser.name || selectedCmsUser.email
            }" to the 3D user "${
              users3d.find((u) => Number(u.id) === Number(selectedOther3dUser))
                ?.name || "Unknown"
            }"?`
          : "Please select users to link."
        }
        confirmLabel="Link Account"
        cancelLabel="Cancel"
        onConfirm={confirmLinkOtherAccount}
        onCancel={() => setConfirmLinkOtherOpen(false)}
        isDanger={false}
      />

      {/* Confirm Unlink Other User's Account Modal */}
      <ConfirmModal
        open={confirmUnlinkOtherOpen}
        title="Confirm Unlink Account"
        message={
          unlinkingCmsUserId ?
            `Are you sure you want to unlink this CMS user from their linked 3D user?`
          : "No account selected for unlinking."
        }
        confirmLabel="Unlink"
        cancelLabel="Cancel"
        onConfirm={confirmUnlinkOtherAccount}
        onCancel={() => {
          setConfirmUnlinkOtherOpen(false);
          setUnlinkingCmsUserId(null);
        }}
        isDanger={true}
      />
    </div>
  );
};

export default Users3DManagementPage;

